import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGrid, FiEdit3, FiScissors, FiLayers, FiArrowRight, FiBriefcase } = FiIcons;

const TeacherHub = ({ updateProgress, setCurrentSection }) => {
  useEffect(() => {
    setCurrentSection('teacher-hub');
    updateProgress('teacher-hub');
  }, []);

  const tools = [
    {
      id: 'matching',
      title: 'Word Matching Game',
      description: 'A fun memory game pairing Amharic words with English meanings to boost vocabulary retention.',
      icon: FiGrid,
      color: 'bg-green-500',
      path: '/teacher-hub/matching',
      badge: 'Interactive'
    },
    {
      id: 'writing',
      title: 'Writing Practice',
      description: 'Digital tracing board for Amharic pronouns and letters with printable guided worksheets.',
      icon: FiEdit3,
      color: 'bg-blue-500',
      path: '/teacher-hub/writing',
      badge: 'Digital + Print'
    },
    {
      id: 'hierarchy',
      title: 'Community Hierarchy',
      description: 'Interactive activity to arrange community roles from casual to formal, teaching cultural respect.',
      icon: FiLayers,
      color: 'bg-purple-500',
      path: '/teacher-hub/hierarchy',
      badge: 'Cultural'
    },
    {
      id: 'printables',
      title: 'Cut & Paste Printables',
      description: 'Downloadable worksheets for offline classroom activities, including word building and sorting.',
      icon: FiScissors,
      color: 'bg-amber-500',
      path: '/teacher-hub/printables',
      badge: 'PDF Ready'
    }
  ];

  return (
    <PageLayout
      title="Teacher & Parent Hub"
      subtitle="Resources, games, and tools designed for educators to reinforce learning in the classroom and at home."
      icon={FiBriefcase}
      color="text-indigo-600"
      bgColor="bg-indigo-50"
      prevLink="/"
      prevLabel="Dashboard"
    >
      <div className="space-y-8 fade-in">
        {/* Hero Section */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-3xl p-8 text-white shadow-xl relative overflow-hidden">
          <div className="relative z-10 max-w-2xl">
            <h2 className="text-3xl font-bold mb-4">Empower Your Teaching</h2>
            <p className="text-indigo-100 text-lg leading-relaxed mb-6">
              Access a suite of interactive tools and printable resources designed to make Amharic social grammar engaging and accessible for children.
            </p>
          </div>
          {/* Decorative Background */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl transform translate-x-20 -translate-y-20"></div>
          <div className="absolute bottom-0 right-20 w-32 h-32 bg-yellow-400 opacity-20 rounded-full blur-2xl"></div>
        </div>

        {/* Tools Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tools.map((tool) => (
            <Link
              key={tool.id}
              to={tool.path}
              className="group bg-white rounded-2xl p-6 border-2 border-gray-100 shadow-sm hover:shadow-xl hover:border-indigo-200 hover:-translate-y-1 transition-all duration-300 flex flex-col"
            >
              <div className="flex justify-between items-start mb-4">
                <div className={`w-14 h-14 rounded-2xl ${tool.color} flex items-center justify-center text-white shadow-lg group-hover:scale-110 transition-transform`}>
                  <SafeIcon icon={tool.icon} className="text-2xl" />
                </div>
                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold uppercase tracking-wider rounded-full">
                  {tool.badge}
                </span>
              </div>
              
              <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-indigo-600 transition-colors">
                {tool.title}
              </h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-6 flex-1">
                {tool.description}
              </p>

              <div className="flex items-center text-indigo-600 font-bold text-sm group-hover:gap-2 transition-all">
                Open Tool <SafeIcon icon={FiArrowRight} className="ml-1" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </PageLayout>
  );
};

export default TeacherHub;